import _ from "underscore";

import { generateTimePrefills } from "./QuickCreateUtils";
import TemplateAPI from "../api/api/TemplateAPI";
import Constants from "./Constants";
import StrUtils from "./StrUtils";
import dayjs from "dayjs";

const complexFieldTypes = [
  "Email",
  "Phone",
  "URL",
  "MultiReference",
  "SingleFile",
  "Location",
];

const partiallyComplexFieldTypes = ["Image", "Audio", "Video"];

export default class FieldUtils {
  // Checks to see if the fieldType passed in has options
  static fieldTypeHasOptions(fieldTypes, apiName) {
    if (!apiName) return false;

    let field = _.findWhere(fieldTypes, { APIName: apiName });

    if (!field) return false;

    return field.SourcesList ? true : false;
  }

  static fieldTypeIsSelect(fieldType) {
    return (
      fieldType === Constants.FIELDTYPES.SINGLE_SELECT ||
      fieldType === Constants.FIELDTYPES.MULTI_SELECT
    );
  }

  static fieldTypeIsCalculated(fieldType) {
    return fieldType === Constants.FIELDTYPES.CALCULATED;
  }

  static fieldTypeIsReference(fieldType) {
    return (
      fieldType === Constants.FIELDTYPES.SINGLE_REFERENCE ||
      fieldType === Constants.FIELDTYPES.MULTI_REFERENCE
    );
  }

  static fieldTypeIsSubitem(fieldType) {
    return (
      fieldType === Constants.FIELDTYPES.SUBITEM ||
      fieldType === Constants.FIELDTYPES.MULTI_SUBITEM
    );
  }

  static fieldTypeIsAttachment(fieldType) {
    return fieldType === Constants.FIELDTYPES.SINGLE_FILE;
  }

  static isComplex(fieldType) {
    return complexFieldTypes.includes(fieldType);
  }

  static isPartiallyComplex(fieldType) {
    return partiallyComplexFieldTypes.includes(fieldType);
  }

  static getFormattedDefaultValue(defaultValue, fieldType) {
    if (defaultValue && typeof formattedDefaultVal !== "boolean") {
      if (fieldType === Constants.FIELDTYPES.TIME) {
        defaultValue = dayjs(defaultValue).format("HH:mmZZ");
      } else if (fieldType === Constants.FIELDTYPES.DATE) {
        defaultValue = defaultValue.includes("{{time")
          ? defaultValue
          : defaultValue.toISOString();
      } else if (fieldType === Constants.FIELDTYPES.DATE_TIME) {
        return defaultValue;
      }
    }
    return defaultValue;
  }

  static objHasValueAtKey(obj, key) {
    if (
      typeof obj !== "object" ||
      obj === null ||
      !obj.hasOwnProperty(key) ||
      obj[key] === null ||
      obj[key] === "" ||
      obj[key]?.length === 0
    ) {
      return false;
    }

    return true;
  }

  /**
   * Determines if the values provided contain the required fields for the module.
   *
   * @param {Object} module - The module to check.
   * @param {Object} values - The values to check.
   */
  static containsRequiredFields(module, values) {
    if (!module) {
      return false;
    }

    let required = _.filter(module?.Fields, { Required: true });
    if (!required?.length) {
      return true;
    }

    for (let i = 0; i < required?.length; i++) {
      const field = required[i]?.APIName;

      if (!field) continue;

      if (!this.objHasValueAtKey(values, field)) return false;
    }

    return true;
  }

  static async createTemplateModuleField(
    fieldValues,
    templateID,
    moduleAPIName
  ) {
    const formattedDefaultVal = FieldUtils.getFormattedDefaultValue(
      fieldValues.defaultValue,
      fieldValues.fieldType
    );

    const payload = {
      Name: fieldValues.name,
      APIName: fieldValues.apiName,
      Description: fieldValues.description ? fieldValues.description : "",
      FieldType: fieldValues.fieldType,
      DefaultValue: formattedDefaultVal ?? null,
      ...((fieldValues.type === "SingleReference" ||
        fieldValues.type === "MultiReference") &&
        fieldValues.defaultSource && {
          DefaultSource: fieldValues.defaultSource,
        }),
      Required: fieldValues.required ? true : false,
      Unique: fieldValues.unique ? true : false,
      SourcesList: fieldValues.source ? fieldValues.source : null,
      ...(fieldValues.type === "Toggle" && {
        TrueDisplayName: fieldValues.trueDisplayName,
      }),
      ...(fieldValues.type === "Toggle" && {
        FalseDisplayName: fieldValues.falseDisplayName,
      }),
      Protected: fieldValues.protected ? true : false,
      RenameProtected: fieldValues.renameProtected ? true : false,
      ReadOnly: fieldValues?.readonly ?? false,
      Sensitive: fieldValues?.sensitive ?? false,
    };

    return TemplateAPI.createCRMTemplateModuleField(
      templateID,
      moduleAPIName,
      payload
    );
  }

  static async modifyTemplateModuleField(
    fieldValues,
    templateID,
    moduleAPIName,
    fieldAPIName
  ) {
    const formattedDefaultVal = FieldUtils.getFormattedDefaultValue(
      fieldValues.defaultValue,
      fieldValues.fieldType
    );

    const payload = {
      Name: fieldValues.name,
      APIName: fieldValues.apiName,
      Description: fieldValues.description ? fieldValues.description : "",
      FieldType: fieldValues.type,
      DefaultValue: formattedDefaultVal ?? null,
      ...((fieldValues.type === "SingleReference" ||
        fieldValues.type === "MultiReference") &&
        fieldValues.defaultSource && {
          DefaultSource: fieldValues.defaultSource,
        }),
      Required: fieldValues.required ? true : false,
      Unique: fieldValues.unique ? true : false,
      SourcesList: fieldValues.source ? fieldValues.source : null,
      ...(fieldValues.type === "Toggle" && {
        TrueDisplayName: fieldValues.trueDisplayName,
      }),
      ...(fieldValues.type === "Toggle" && {
        FalseDisplayName: fieldValues.falseDisplayName,
      }),
      Protected: fieldValues.protected ? true : false,
      RenameProtected: fieldValues.renameProtected ? true : false,
      ReadOnly: fieldValues?.readonly ?? false,
      Sensitive: fieldValues?.sensitive ?? false,
    };

    return TemplateAPI.modifyCRMTemplateModuleField(
      templateID,
      moduleAPIName,
      fieldAPIName,
      payload
    );
  }

  static createPageNamePayload(pageTitle, sections) {
    let payload = {
      PageTitle: pageTitle,
      Sections: [],
    };

    for (let i = 0; i < sections.length; i++) {
      let sectData = sections[i];

      let sect = {
        Title: sectData.Title,
        Description: sectData.Description ? sectData.Description : "",
        Fields: [],
      };

      for (let j = 0; j < sectData.Fields.length; j++) {
        const f = sectData.Fields[j];

        sect.Fields.push({
          Field: f.Field.APIName,
          ColSpan: 1,
        });
      }

      payload.Sections.push(sect);
    }

    return payload;
  }

  static getUpdatedPageTitle(currentModule, oldAPIName, newAPIName, sections) {
    let pageTitle = currentModule?.Templates?.RecordDetailView?.PageTitle;

    if (pageTitle && pageTitle.includes(oldAPIName)) {
      const toBeReplaced = `{{${oldAPIName}}}`;
      const regex = new RegExp(toBeReplaced, "g");

      pageTitle = pageTitle.replace(regex, `{{${newAPIName}}}`);

      let payload = this.createPageNamePayload(pageTitle, sections);

      return payload;
    }

    return null;
  }

  static isReference(field) {
    const fieldType = field?.FieldType;

    if (!fieldType) return false;

    return (
      fieldType === Constants.FIELDTYPES.SINGLE_REFERENCE ||
      fieldType === Constants.FIELDTYPES.MULTI_REFERENCE
    );
  }

  static getUpdatedTableLayout(currentModule, oldAPIName, newAPIName) {
    let tableLayout = currentModule?.Templates?.RecordListView?.Fields;

    if (tableLayout && tableLayout.includes(oldAPIName)) {
      return tableLayout.map((item) => {
        if (item === oldAPIName) return newAPIName;
        return item;
      });
    }

    return null;
  }

  static getTemplateVariables(template) {
    const variableRegex = /\{\{(.+?)\}\}/g;

    if (!template) {
      return template;
    }

    const templateVariables = [...template.matchAll(variableRegex)].map(
      (matches) => matches[1]
    );

    return templateVariables;
  }

  // Extract variables from template, remove all that aren't defined in the record.
  static removeUndefinedVariablesFromTemplate(template, record) {
    if (!record) return template;

    const templateVariables = this.getTemplateVariables(template);

    // Exit loop early if there are no template variables.
    if (!templateVariables || templateVariables?.length === 0) return template;

    const recordKeys = Object.keys(record);

    const undefinedVariables = _.filter(
      templateVariables,
      (variable) =>
        recordKeys.includes(variable) === false ||
        !record[variable] ||
        record[variable].length === 0
    );

    let newTemplate = template;

    for (const variable of undefinedVariables) {
      const specificVariableRegex = new RegExp(`\{\{${variable}\}\}`, "g");
      newTemplate = newTemplate.replace(specificVariableRegex, "");
    }

    return newTemplate;
  }

  static getModuleByAPIName(modules, moduleAPIName) {
    if (!modules?.length) return null;

    let module = _.findWhere(modules, {
      APIName: moduleAPIName,
    });

    return module;
  }

  static generateDefaultValue(template, variableValues) {
    if (template === "" || template === undefined || template === null) {
      return null;
    }

    if (_.isArray(template) || _.isObject(template) || _.isBoolean(template)) {
      return template;
    }

    try {
      _.templateSettings = {
        interpolate: /\{\{(.+?)\}\}/g,
      };

      if (template.startsWith("{{time.")) {
        const returnValue = new Date(
          generateTimePrefills({ value: template })?.Value
        );

        return returnValue?.Default || returnValue?.Value;
      }
      let valTemplate = _.template(template);

      return valTemplate(variableValues);
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  static getFieldFromModule(
    module,
    fieldAPIName,
    createMode = false,
    prefill = null,
    variableValues = null,
    readonlyPrefill = null
  ) {
    if (!module) return null;

    if (fieldAPIName === "Created") {
      return {
        Name: "Created",
        APIName: "Created",
        FieldType: "DateTime",
        SystemField: true,
        ReadOnly: true,
      };
    }

    if (fieldAPIName === "id") {
      return {
        Name: "Record ID",
        APIName: "id",
        SystemField: true,
        ReadOnly: true,
        FieldType: "Text",
      };
    }

    if (fieldAPIName === "LastUpdated") {
      return {
        Name: "Last Modified",
        APIName: "LastUpdated",
        FieldType: "DateTime",
        SystemField: true,
        ReadOnly: true,
      };
    }

    let field = _.findWhere(module.Fields, {
      APIName: fieldAPIName,
    });

    if (!field) return null;

    if (!createMode) return field;

    field = _.clone(field);

    if (field.DefaultValue) {
      field.DefaultValue = this.generateDefaultValue(
        field.DefaultValue,
        variableValues
      );
    }

    if (prefill && _.isArray(prefill)) {
      let thePrefill = _.findWhere(prefill, { Field: fieldAPIName });

      if (thePrefill) {
        field.DefaultValue = thePrefill.Value;
      }
    }

    if (readonlyPrefill && _.isArray(readonlyPrefill)) {
      const foundPrefill = _.findWhere(readonlyPrefill, {
        Field: fieldAPIName,
      });

      if (foundPrefill) {
        field.DefaultValue = foundPrefill.Value;
        field.ReadOnly = true;
      }
    }

    return field;
  }

  static getModuleTypeBySourcesList(sourcesList) {
    if (sourcesList?.includes("Global.Lists.")) {
      return "Global-Lists";
    } else if (sourcesList === "Any") {
      return "Dynamic-Module";
    } else if (
      sourcesList === "Tenant.Settings.Users" ||
      sourcesList === "Settings.Users"
    ) {
      return "Settings-Users";
    } else {
      return "Module";
    }
  }

  static getModuleNameFromSource(sourceString) {
    if (!sourceString) return null;

    let moduleAPIName = null;

    if (sourceString.indexOf("Tenant.Modules.") >= 0) {
      moduleAPIName = sourceString.replace("Tenant.Modules.", "");
    }

    return moduleAPIName;
  }

  static getSourceModuleForField(modules, sourcesList) {
    if (!sourcesList) return null;

    let source = "";

    // Legacy support - Old SourcesLists were formatted `Tenant.${ModuleName}`
    // There was no Modules / Settings in between the module name
    if (
      sourcesList &&
      sourcesList.indexOf("Tenant.") >= 0 &&
      sourcesList.indexOf("Modules") < 0 &&
      sourcesList.indexOf("Settings") < 0
    ) {
      source = sourcesList.replace("Tenant.", "");
    } else if (typeof sourcesList === "string") {
      // Modules
      if (sourcesList && sourcesList.indexOf("Tenant.Modules.") >= 0) {
        source = sourcesList.replace("Tenant.Modules.", "");
      } else if (
        sourcesList &&
        sourcesList.indexOf("Tenant.Settings.Users") >= 0
      ) {
        // Users
        source = sourcesList.replace("Tenant.", "");
      } else if (sourcesList && sourcesList.indexOf("Global.Lists") >= 0) {
        source = sourcesList.replace("Global.Lists.", "");
      }
    }

    // If the sourcelist is from another Tenant
    if (sourcesList.split(".")[0] !== "Tenant") {
      source = sourcesList.split(".")[2];
    }

    // TODO: continue work on this.

    if (source === "Settings.Users") {
      return {
        Name: "CRM Users",
        APIName: "Settings.Users",
      };
    }

    const isPublicForm = window.location.pathname.startsWith("/forms");

    if (isPublicForm) {
      return { Name: source, APIName: source, Fields: [] };
    }

    return this.getModuleByAPIName(modules, source);
  }

  static handleExistingPrefill = (module, existingPrefill) => {
    const newSections = [];
    const newSelectedFields = [];

    for (const prefill of existingPrefill) {
      const APIName = prefill.Field;
      const splitField = APIName.split(".");
      let Field;

      if (splitField.length > 1) {
        const parentField = _.findWhere(module.Fields, {
          APIName: splitField[0],
        });

        Field = _.findWhere(parentField.SubitemFields, {
          APIName: splitField[1],
        });
      } else {
        Field = _.findWhere(module.Fields, { APIName: prefill.Field });
      }

      const Value = prefill.Value;
      const usesVariables = typeof Value === "string" && Value.includes("{{");

      newSections.push({ APIName, Field, Value, usesVariables });
      newSelectedFields.push(APIName);
    }

    return { newSections, newSelectedFields };
  };

  static handleExistingMultiSubitemPrefill = (fieldSchema, existingPrefill) => {
    const newSections = [];
    const newSelectedFields = [];

    for (const prefill of existingPrefill) {
      const APIName = prefill.Field;

      const Field = _.findWhere(fieldSchema.SubitemFields, {
        APIName: APIName,
      });

      const Value = prefill.Value;
      const usesVariables = typeof Value === "string" && Value.includes("{{");

      newSections.push({ APIName, Field, Value, usesVariables });
      newSelectedFields.push(APIName);
    }

    return { newSections, newSelectedFields };
  };

  static getDefaultFields = (module) => {
    return [
      {
        Name: "Record Name",
        APIName: "_RecordName",
        FieldType: "Text",
        ReadOnly: true,
      },
      {
        Name: "Created",
        APIName: "Created",
        FieldType: "DateTime",
        ReadOnly: true,
      },
      {
        Name: "Modified",
        APIName: "LastUpdated",
        FieldType: "DateTime",
        ReadOnly: true,
      },
      {
        Name: "Created By",
        APIName: "CreatedBy",
        FieldType: "SingleReference",
        SourcesList: "Tenant.Modules." + module?.APIName,
        ReadOnly: true,
      },
      {
        Name: "Last Modified By",
        APIName: "LastUpdatedBy",
        FieldType: "SingleReference",
        SourcesList: "Tenant.Modules." + module?.APIName,
        ReadOnly: true,
      },
      {
        Name: "Tags",
        APIName: "Tags",
        FieldType: "Tags",
        SourcesList: module?.Tags?.map((tag) => ({
          Name: tag.Name,
          Value: tag.id,
        })),
        ReadOnly: false,
      },
    ];
  };

  // t = the type of the field
  static fieldUsesDefault = (t) => {
    return (
      t === Constants.FIELDTYPES.NUMBER ||
      t === Constants.FIELDTYPES.PERCENT ||
      t === Constants.FIELDTYPES.EMAIL ||
      t === Constants.FIELDTYPES.TEXT ||
      t === Constants.FIELDTYPES.LONG_TEXT ||
      t === Constants.FIELDTYPES.RICH_TEXT ||
      t === Constants.FIELDTYPES.URL ||
      t === Constants.FIELDTYPES.MULTI_SELECT ||
      t === Constants.FIELDTYPES.SINGLE_SELECT ||
      t === Constants.FIELDTYPES.PHONE ||
      t === Constants.FIELDTYPES.TIME ||
      t === Constants.FIELDTYPES.DATE ||
      t === Constants.FIELDTYPES.DATE_TIME ||
      t === Constants.FIELDTYPES.CURRENCY ||
      t === Constants.FIELDTYPES.CALCULATED ||
      t === Constants.FIELDTYPES.CURRENCY_INTERVAL ||
      t === Constants.FIELDTYPES.OBJECT ||
      t === Constants.FIELDTYPES.TOGGLE ||
      t === Constants.FIELDTYPES.SINGLE_REFERENCE ||
      t === Constants.FIELDTYPES.MULTI_REFERENCE ||
      t === Constants.FIELDTYPES.COLOR
    );
  };

  /**  For looping over Subitem Fields, adding them as Filter options.
   * @param {Object} field - The field to check.
   * @param {Funcion} optionComponent - Function that generates the option component
   *                                    name, value will be passed in as parameters.
   **/
  static getSubitemOptions = (field, optionComponent) => {
    if (!this.fieldTypeIsSubitem(field.FieldType)) return null;

    const subitemOptions = [];

    const parentFieldName = field.Name;
    const parentAPIName = field.APIName;

    // Loop over Subitem Fields, adding them as Filter options.
    for (let i = 0; i < field.SubitemFields.length; i++) {
      const subitemField = field.SubitemFields[i];

      subitemOptions.push(
        optionComponent(
          `${subitemField.Name}`,
          `${parentAPIName}.${subitemField.APIName}`,
          parentFieldName
        )
      );
    }

    return subitemOptions;
  };

  // Used for field types that contain an array on items
  // Grabs either the `Primary` value or the first value in the array.
  // recordField - The record field to search. (Should be array)
  // key - The key of the value to grab after the item is found.
  // primaryKey - The Primary key.
  static getPrimaryOrFirstValue = (
    recordField,
    key,
    primaryKey = "Primary"
  ) => {
    if (!recordField || !key || !Array.isArray(recordField)) return "";

    let foundItem = _.findWhere(recordField, { [primaryKey]: true });

    if (foundItem) return foundItem?.[key] ?? "";

    return recordField?.[0]?.[key] ?? "";
  };

  /**  For looping over Record Summary Fields, adding them as Filter options.
   * @param {Object} field - The field to check.
   * @param {Funcion} optionComponent - Function that generates the option component
   *                                    name, value will be passed in as parameters.
   **/
  static getRecordSummaryOptions = (field, optionComponent) => {
    if (
      (field.FieldType === "SingleReference" ||
        field.FieldType === "MultiReference") &&
      field.RecordSummary
    ) {
      const recordSummaryOptions = [];
      for (let i = 0; i < Object.keys(field.RecordSummaryFields).length; i++) {
        const summarySource = Object.keys(field.RecordSummaryFields)[i];
        for (
          let j = 0;
          j < field.RecordSummaryFields[summarySource].Fields.length;
          j++
        ) {
          const summaryField =
            field.RecordSummaryFields[summarySource].Fields[j];
          recordSummaryOptions.push(
            optionComponent(
              `${field.Name}: ${summaryField}`,
              `${field.APIName}.${summaryField}`,
              `${summarySource}`
            )
          );
        }
      }
      return recordSummaryOptions;
    }

    return null;
  };

  /**  Validate Password Input.
   * @param {String} password - The value to check.
   * (?=.{8,}) - 8 or more characters long
   * (^(.)\1+$) - All letters are the same
   **/
  static validatePasswordInput = (password, userEmail) => {
    if (!userEmail) {
      return {
        valid: false,
        message: "Please enter your email first before entering your password.",
      };
    }

    if (!password.match(/(?=.{8,})/)) {
      return {
        valid: false,
        message: "Password needs to be at least 8 characters.",
      };
    }

    if (
      password.toLowerCase().includes("password") ||
      password.toLowerCase().includes("qwerty") ||
      /^(.)\1+$/.test(password)
    ) {
      return {
        valid: false,
        message: "Insecure password, try with another one.",
      };
    }

    if (
      password.toLowerCase().includes("growth") ||
      password.toLowerCase().includes("growthcloud")
    ) {
      return {
        valid: false,
        message:
          "Password can't contain the word 'growth' or 'growthcloud' in it.",
      };
    }

    if (password.toLocaleLowerCase().includes(userEmail.split("@")[0])) {
      return {
        valid: false,
        message: "Password can't be similar to your user email.",
      };
    }

    return {
      valid: true,
      message: "Password meets very basic requirements.",
    };
  };

  /**  Get Initials for Record FirstName and LastName.
   * @param {Record<string, any> | undefined} record - Record from where we get FirstName and Last Name.
   * If Record doesnt have FirstName and LastName will return NaN
   **/
  static getInitials = (record) => {
    let firstInitial = null;
    let lastInitial = null;

    if (!record) return;

    const recordName = record._RecordName;

    if (recordName) {
      const splitName1 = recordName.split(" ")[0];
      const splitName2 = recordName.split(" ")[1];

      const matches1 = splitName1 ? splitName1.match(/[a-zA-Z]/) : null;
      const matches2 = splitName2 ? splitName2.match(/[a-zA-Z]/) : null;
      firstInitial = matches1 ? matches1[0].toUpperCase() : "";
      lastInitial = matches2 ? matches2[0].toUpperCase() : "";

      return `${firstInitial}${lastInitial}`;
    } else {
      return "";
    }
  };

  /**  Create an Image with Record Initials.
   * @param {Number} size - Size of image.
   * @param {Object} record - Record from where we get FirstName and Last Name..
   * @param {String} color - Color.
   **/
  static createLetterProfileImage = (size, record, color, mode = "Avatar") => {
    if (!record) return {};

    let initials = this.getInitials(record);

    const darkColor = StrUtils.shadeColor(color, -30);
    const bgColor = StrUtils.shadeColor(color, 25);

    const widgetStyle = {
      borderRadius: mode !== "Avatar" ? 2 : "100%",
      fontSize: 10,
      fontWeight: 500,
      backgroundColor: bgColor ?? "var(--primary)",
      color: darkColor,
      border: `1px solid ${darkColor}`,
      height: size?.height ?? 25,
      width: size?.width ?? 25,
      display: "inline-block",
      marginLeft: size?.marginLeft ?? "0.025rem",
      position: "relative",
    };

    return {
      styles: widgetStyle,
      initials: initials,
    };
  };

  /**
   * Generate a Random Color.
   **/
  static getRandomColor = () => {
    const randomInt = (min, max) => {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    const hslToHex = (h, s, l) => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0"); // convert to Hex and prefix "0" if needed
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    const hexColor = () => {
      var h = randomInt(0, 360);
      var s = randomInt(42, 98);
      var l = randomInt(40, 90);

      return hslToHex(h, s, l);
    };

    return hexColor();
  };

  static getContactEmail = (contact) => {
    const primaryEmail = this.getPrimaryOrFirstValue(contact.Email, "Email");
    return primaryEmail;
  };

  static getFieldsFromModuleOrSubitem = (data) => {
    return data.Fields || data.SubitemFields;
  };

  /**
   * Return the valid fields
   * @param  module
   * @param calculatedType
   * @param skipField field APIName to skip in valid calculated field array.
   */
  static validCalculatedField = (module, calculatedType, skipField = "") => {
    const validFields = [];
    let validFieldTypes;

    if (!module || !calculatedType) return validFields;

    const availableFields = module.Fields ? module.Fields : [];

    if (calculatedType === "string") {
      validFieldTypes = ["Text", "LongText", "RichText"];
    }

    if (calculatedType === "number") {
      validFieldTypes = ["Number", "Percent", "Currency"];
    }

    for (const field of availableFields) {
      if (!validFieldTypes.includes(field.FieldType)) {
        continue;
      }

      // For fields (Number, Percent, Currency) which are using the Calculated Formula
      if (skipField && field?.APIName === skipField) continue;

      // Skipping another fields (Number, Percent, Currency) which are using a calculated Formula as Default Value.
      if (field?.CalculatedFieldData && !_.isEmpty(field?.CalculatedFieldData))
        continue;

      validFields.push(field);
    }

    return validFields;
  };

  // Adds default fields to all modules in the schema
  static addDefaultsToSchema(schema, readOnly = false) {
    if (schema?.CRM?.DefaultsAdded) return;

    for (let i = 0; i < schema?.CRM?.Modules?.length; i++) {
      const module = schema.CRM.Modules[i];

      let subFields = _.filter(module?.Fields, (field) => {
        return (
          field?.FieldType === "MultiSubitem" || field?.FieldType === "Subitem"
        );
      });

      if (subFields.length) {
        // Applaying default field to MultiSubitem and Subitem fields

        for (let j = 0; j < subFields.length; j++) {
          let subField = subFields[j];

          if (
            subField?.FieldType !== "Subitem" &&
            subField?.FieldType !== "MultiSubitem"
          )
            continue;

          const newSubDefaultFields = Constants.SUB_DEFAULT_FIELDS(
            readOnly
          ).map((f) => {
            const cloneF = structuredClone(f);
            cloneF.SubitemParent = subField.APIName;
            return cloneF;
          });

          subField?.SubitemFields?.push(...newSubDefaultFields);
        }
      }

      if (Array.isArray(module?.Fields)) {
        if (["Conversations", "Notifications"].includes(module.APIName)) {
          module.Fields.push(...Constants.BASIC_DEFAULT_FIELDS());
        } else {
          module.Fields.push(...Constants.DEFAULT_FIELDS(readOnly));
        }
      }
    }

    if (schema.CRM) {
      schema.CRM.DefaultsAdded = true;
    }
  }

  static getSourceListDataSources({ sourceList }) {
    if (!sourceList) return [];

    const sourceListArray = Array.isArray(sourceList);

    if (!sourceListArray) {
      if (sourceList === "Any") {
        return "Any";
      } else {
        return [sourceList.split(".")[2]];
      }
    } else {
      let dataSources = [];
      for (let i = 0; i < sourceList.length; i++) {
        const sourceListItem = sourceList[i];
        const dataSource = sourceListItem.split(".")[2];
        dataSources.push(dataSource);
      }
      return dataSources;
    }
  }

  /**
   * Gets all field schemas that matches with the `fieldTypes` array, including `Subitem` and `MultiSubitem` fields.
   * Returns an array of field schemas.
   * @param fields Default fields are appended.
   * @param fieldType The Field Types to match.
   */
  static getFieldsBaseOnType(fields, fieldTypes) {
    const fieldMatches = [];

    if (!fields?.length) return fieldMatches;

    for (const field of fields) {
      if (
        field?.FieldType === "Subitem" ||
        field?.FieldType === "MultiSubitem"
      ) {
        if (!field?.SubitemFields?.length) continue;

        const subitemMatches = this.getFieldsBaseOnType(
          (field?.SubitemFields || [])?.concat(this.getDefaultFields()),
          fieldTypes
        );

        if (!subitemMatches?.length) continue;

        fieldMatches.push({ ...field, SubitemFields: subitemMatches });

        continue;
      }

      if (fieldTypes?.includes(field?.FieldType)) {
        fieldMatches.push(field);
      }
    }

    return fieldMatches;
  }
}
